import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { TrendingUp, AlertCircle, Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/auth.store'

export const Login = () => {
  const navigate = useNavigate()
  const setAuth = useAuthStore(state => state.setAuth)
  const isHydrated = useAuthStore(state => state.isHydrated)
  const isAuthenticated = useAuthStore(state => state.checkAuth())
  
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isHydrated && isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isHydrated, isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      // OAuth2PasswordRequestForm requires application/x-www-form-urlencoded
      const formData = new URLSearchParams()
      formData.append('username', email)
      formData.append('password', password)

      const response = await fetch(`/api/v1/auth/login?remember_me=${rememberMe}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.detail || 'Login failed')
      }

      const data = await response.json()
      
      // Store auth data in Zustand
      setAuth({
        id: data.user_id || 1,
        email: email,
        is_admin: data.is_admin || false,
        subscription_status: data.subscription_status || 'inactive',
        subscription_end_date: data.subscription_end_date || null,
        emergency_mode: data.emergency_mode || false,
        has_paid_initial_fee: data.has_paid_initial_fee || false
      }, data.access_token)

      // Redirect to dashboard
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid credentials')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-10 w-10 text-primary" />
              <span className="text-2xl font-bold">TradeBridgePro</span>
            </div>
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl">Welcome Back</CardTitle>
            <CardDescription>Sign in to your account to continue</CardDescription>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-destructive/10 text-destructive rounded-md">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="remember-me"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
              />
              <Label 
                htmlFor="remember-me" 
                className="text-sm font-normal cursor-pointer"
              >
                Ricordami per 30 giorni
              </Label>
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accesso in corso...
                </>
              ) : (
                'Accedi'
              )}
            </Button>

            <div className="flex items-center justify-between text-sm">
              <Link 
                to="/register" 
                className="text-primary hover:underline"
              >
                Non hai un account? Registrati
              </Link>
              <Link 
                to="/reset-password" 
                className="text-primary hover:underline"
              >
                Password dimenticata?
              </Link>
            </div>

            <div className="text-center text-sm text-muted-foreground">
              <p>Protected by enterprise-grade security</p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
